export type ColorTheme = {
  background_color: string
  headline_color: string
  body_color: string
  accent_colors: string[]
  accent_gradients?: string[]
  logo?: string
  logo_natural_width?: number
  logo_natural_height?: number
}

export type FontWeight = {
  weight: number
  style?: "normal" | "italic"
  url?: string
}

export type FontSpec = {
  family: string
  weights: FontWeight[]
  allcaps?: boolean
}

export type Fonts = {
  headline_font: FontSpec
  body_font: FontSpec
}

export type VisualProfileSpec = Fonts & {
  brand_name?: string
  light_theme?: ColorTheme
  dark_theme?: ColorTheme
  notes?: string
}

// An inline SVG needs an xmlns declaration to render via <img src="data:image/svg+xml…">.
const withSvgXmlns = (svg: string): string =>
  /<svg\b[^>]*\sxmlns\s*=/i.test(svg) ? svg : svg.replace(/<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"')

// A logo is either a URL or inline SVG markup; the SVG becomes a data URI so an <img> renders it
// and can't execute scripts from the generated markup.
export const logoImageSrc = (logo: string | null | undefined): string | undefined => {
  if (!logo) return undefined
  return logo.trimStart().startsWith("<svg") ? `data:image/svg+xml;utf8,${encodeURIComponent(withSvgXmlns(logo))}` : logo
}

export const fontFamilyExpr = (family: string): string => {
  const trimmed = family.trim()
  const primary = trimmed.includes(",") ? trimmed : `'${trimmed}'`
  return `${primary}, sans-serif`
}

const primaryFamilyName = (family: string): string =>
  family.replace(/['"]/g, "").split(",")[0].trim()

const GENERIC_FAMILIES = new Set(["sans-serif", "serif", "monospace", "system-ui"])

export const buildGoogleFontsUrl = (fonts: Fonts): string | null => {
  const merged = new Map<string, Set<number>>()
  for (const fontSpec of [fonts.headline_font, fonts.body_font]) {
    const family = primaryFamilyName(fontSpec.family)
    if (!family || GENERIC_FAMILIES.has(family.toLowerCase())) continue
    const weights = fontSpec.weights.filter(w => !w.url).map(w => w.weight)
    if (weights.length === 0) continue
    if (!merged.has(family)) merged.set(family, new Set())
    for (const w of weights) merged.get(family)!.add(w)
  }
  if (merged.size === 0) return null
  const parts = [...merged.entries()].map(([family, weights]) => {
    const sorted = [...weights].sort((a, b) => a - b)
    return `family=${encodeURIComponent(family)}:wght@${sorted.join(";")}`
  })
  return `https://fonts.googleapis.com/css2?${parts.join("&")}&display=swap`
}

export const measureLogo = async (file: File): Promise<{ width: number; height: number }> => {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.src = url
  try {
    await img.decode()
  } finally {
    URL.revokeObjectURL(url)
  }
  if (img.naturalWidth === 0 || img.naturalHeight === 0) throw new Error("Logo has no intrinsic dimensions")
  return { width: img.naturalWidth, height: img.naturalHeight }
}

export const buildSelfHostedFontFaces = (fonts: Fonts): string => {
  const faces: string[] = []
  for (const fontSpec of [fonts.headline_font, fonts.body_font]) {
    for (const w of fontSpec.weights) {
      if (!w.url) continue
      faces.push(
        `@font-face { font-family: '${fontSpec.family}'; src: url('${w.url}') format('woff2'); ` +
        `font-weight: ${w.weight}; font-style: ${w.style ?? "normal"}; font-display: swap; }`
      )
    }
  }
  return faces.join("\n")
}

export const uploadFileToSignedUrl = async (signedUrl: string, file: File): Promise<void> => {
  const response = await fetch(signedUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file })
  if (!response.ok) throw new Error(`Upload failed (HTTP ${response.status})`)
}
